/**
 * Fullscreen loading affordance shown over the teleport cloud cover while a section
 * streams in: a spinner, a status line, and a Cancel button. The cloud gradient itself
 * is owned by `TeleportCinematic` (`#teleport-clouds`); this sits above it (`z-index:120`)
 * and is otherwise click-through so the location bar + players list (lifted via the
 * `body.loading` class) stay usable for redirecting the load.
 */
export class LoadingOverlay {
  private root: HTMLDivElement;
  private textEl: HTMLDivElement;
  private cancelBtn: HTMLButtonElement;

  /** Invoked when the user clicks Cancel. */
  onCancel: (() => void) | null = null;

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'loading-ui';
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading…</div>
      <button type="button" class="loading-cancel">Cancel</button>
    `;
    this.textEl = this.root.querySelector('.loading-text') as HTMLDivElement;
    this.cancelBtn = this.root.querySelector('.loading-cancel') as HTMLButtonElement;
    this.cancelBtn.addEventListener('click', () => this.onCancel?.());
    document.body.appendChild(this.root);
  }

  /** Show the spinner + Cancel and lift the HUD above the clouds. */
  show(text = 'Loading…'): void {
    this.root.hidden = false;
    this.root.classList.remove('idle');
    this.textEl.textContent = text;
    document.body.classList.add('loading');
  }

  /** Hide everything and drop the HUD back to its normal stacking. */
  hide(): void {
    this.root.hidden = true;
    this.root.classList.remove('idle');
    document.body.classList.remove('loading');
  }

  /** Stay covered with the HUD lifted, but no spinner/Cancel — used after cancelling
   *  the first-ever load when there's no previous location to reveal. */
  idle(text = 'Choose a location or player'): void {
    this.root.hidden = false;
    this.root.classList.add('idle');
    this.textEl.textContent = text;
    document.body.classList.add('loading');
  }
}
